import React, { useState, useEffect, useRef } from 'react';
import './CounterAnimation.css';

const CounterAnimation = ({ targetNumber, suffix = '', duration = 2000, startDelay = 0 }) => {
  const [currentNumber, setCurrentNumber] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      let startTime = null;
      const startNumber = 0;
      const endNumber = parseFloat(targetNumber.toString().replace(/[^0-9.]/g, ''));

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const current = startNumber + (endNumber - startNumber) * easeOutQuart;
        
        setCurrentNumber(current);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCurrentNumber(endNumber);
        }
      };

      requestAnimationFrame(animate);
    }, startDelay);

    return () => clearTimeout(timer);
  }, [isVisible, targetNumber, duration, startDelay]);

  const formatNumber = (num) => {
    if (targetNumber.toString().includes('K')) {
      return Math.floor(num) + 'K';
    } else if (targetNumber.toString().includes('%')) {
      return Math.floor(num) + '%';
    } else if (targetNumber.toString().includes('/')) {
      return targetNumber; // Keep 24/7 as is
    }
    return Math.floor(num);
  };

  return (
    <span ref={elementRef} className="counter-animation">
      {targetNumber.toString().includes('/') ? targetNumber : formatNumber(currentNumber)}
      {suffix}
    </span>
  );
};

export default CounterAnimation;